import {
  QBox,
  QButton,
  QCheckbox,
  QFormControl,
  QModal,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  useCurrentUser,
} from '@qualio/ui-components';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { useExport } from '@qualio/export-components';
import { TemplateDetail } from '../../../../types';
import { buildExportDocTemplatesPayload } from '../exportUtils';
import { exportServiceClient } from '../../../../services';

interface TemplateExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: TemplateDetail;
}

export const TemplateExportModal = ({ isOpen, onClose, template }: TemplateExportModalProps) => {
  const currentUser = useCurrentUser();
  const [coverPageEnabled, setCoverPageEnabled] = useState(true);

  const { trigger, isLoading } = useExport(exportServiceClient, {
    toastTitle: `Exporting ${template.prefix} ${template.name}`,
    'data-cy': 'toast-export-template',
  });

  const onCoverPageChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setCoverPageEnabled(evt.target.checked);
  };

  const reset = useCallback(() => {
    setCoverPageEnabled(true);
  }, []);

  const onCloseCallback = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleExport = useCallback(() => {
    trigger(
      buildExportDocTemplatesPayload([template], currentUser.companyId, currentUser.fullName, false, {
        coverPageEnabled,
      }),
    );
    onCloseCallback();
  }, [coverPageEnabled, template, currentUser.companyId, currentUser.fullName, trigger, onCloseCallback]);

  return (
    <QModal onClose={onCloseCallback} isOpen={isOpen} size="xl" data-cy="template-export-modal">
      <QModalHeader>
        <QText>Export document template</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing={6}>
          <QText fontSize="md">Qualio exports document templates as a PDF.</QText>
          <QFormControl
            label="Cover page"
            helper="A cover page includes document template properties as the first page of
                  the template export."
          >
            <QCheckbox
              isChecked={coverPageEnabled}
              onChange={onCoverPageChange}
              onClick={onCoverPageChange}
              data-cy="export-modal-coverpage-selection"
            >
              Include cover page
            </QCheckbox>
          </QFormControl>
        </QStack>
        <QBox mt={5} mb={3} display="flex" flexDirection="row" justifyContent="flex-end">
          <QBox mr={3}>
            <QButton variant="outline" onClick={onCloseCallback}>
              Cancel
            </QButton>
          </QBox>
          <QButton isLoading={isLoading} onClick={handleExport} data-cy="export-template-button">
            Export
          </QButton>
        </QBox>
      </QModalBody>
    </QModal>
  );
};
